/**
 * AI Confidence Thresholds
 * Source of truth: CategorySuggestion.php in API
 * These define the boundaries for High/Medium/Low confidence levels
 */
export const AI_CONFIDENCE_THRESHOLDS = {
    HIGH: 85,      // >= 85% is High confidence
    MEDIUM: 70     // >= 70% is Medium confidence, < 70% is Low
} as const;

/**
 * AI Confidence Colors
 * Used consistently across the UI for confidence indicators
 */
export const AI_CONFIDENCE_COLORS = {
    HIGH: '#0A725A',    // Green
    MEDIUM: '#CC5602',  // Orange
    LOW: '#BE1238'      // Red
} as const;

/**
 * Get confidence level text based on a confidence score (0-100)
 * Source of truth: CategorySuggestion.php in API
 */
export function getConfidenceLevel(confidence: number | null): string | null {
    if (confidence == null) {
        return null;
    }
    if (confidence >= AI_CONFIDENCE_THRESHOLDS.HIGH) {
        return 'High';
    }
    if (confidence >= AI_CONFIDENCE_THRESHOLDS.MEDIUM) {
        return 'Medium';
    }
    return 'Low';
}

export interface OverviewMetrics {
    totalSuggestions: number;
    acceptedSuggestions: number;
    rejectedSuggestions: number;
    pendingSuggestions: number;
    acceptanceRate: number;
    averageConfidenceScore: number;
    averageProcessingTimeMs: number;
    timeSavedHours: number;
}

export interface AccuracyMetrics {
    categoryAccuracyRate: number;
    vendorAccuracyRate: number;
    highConfidenceAccuracy: number;
    lowConfidenceAccuracy: number;
}

export interface SuggestionsBySource {
    aiModel: number;
    geminiFewshot: number;
    ruleBased: number;
    manual: number;
    batchOperation: number;
}

export interface ConfidenceBucket {
    range: string;
    count: number;
    acceptanceRate: number;
}

export interface SimpleConfidenceBucket {
    label: string;
    count: number;
}

export interface SimpleConfidenceDistribution {
    high: SimpleConfidenceBucket;
    medium: SimpleConfidenceBucket;
    low: SimpleConfidenceBucket;
}

export interface ConfidenceDistribution {
    simple: SimpleConfidenceDistribution;
    detailed: {
        veryHigh: ConfidenceBucket;
        high: ConfidenceBucket;
        medium: ConfidenceBucket;
        low: ConfidenceBucket;
        veryLow: ConfidenceBucket;
    };
    // Legacy fields for backwards compatibility
    veryHigh: ConfidenceBucket;
    high: ConfidenceBucket;
    medium: ConfidenceBucket;
    low: ConfidenceBucket;
    veryLow: ConfidenceBucket;
}

export interface DailyDataPoint {
    date: string;
    suggestions: number;
    accepted: number;
    rejected: number;
    acceptanceRate: number;
}

export interface TimeSeriesData {
    daily: DailyDataPoint[];
}

export interface CategoryStats {
    categoryId: number;
    categoryName: string;
    count: number;
    percentage?: number;
    acceptanceRate?: number;
    rejectionRate?: number;
}

export interface TopCategories {
    mostSuggested: CategoryStats[];
    mostAccepted: CategoryStats[];
    mostRejected: CategoryStats[];
}

export interface UserEngagement {
    averageTimeToDecisionSeconds: number;
    autoAcceptedCount: number;
    manuallyReviewedCount: number;
}

export interface ReasonTypeStats {
    type: string;
    count: number;
    averageInfluence: number;
}

export interface ReasoningInsights {
    topReasonTypes: ReasonTypeStats[];
}

export interface AIStatisticsData {
    overview: OverviewMetrics;
    accuracyMetrics: AccuracyMetrics;
    suggestionsBySource: SuggestionsBySource;
    confidenceDistribution: ConfidenceDistribution;
    timeSeriesData: TimeSeriesData;
    topCategories: TopCategories;
    userEngagement: UserEngagement;
    reasoningInsights: ReasoningInsights;
}

export interface AIStatisticsMeta {
    dateRange: {
        from: string;
        to: string;
    };
    generatedAt: string;
    organizationId: number;
}

export interface AIStatisticsResponse {
    data: AIStatisticsData;
    meta: AIStatisticsMeta;
}

// Display interfaces for the component
export interface CategoryDisplayItem {
    categoryId: number;
    categoryName: string;
    count: number;
    percentage: number;
    color: string;
}
